import { useCallback, useEffect } from 'react';

/* ============================================================
   TASK-326 — shared dismiss behaviour for a per-row "..." action popover
   (Brand Kit's asset kebab today — src/screens/BrandKitScreen.jsx; any
   future kebab/dropdown menu can reuse this). Without it a menu stays open
   until one of its OWN buttons is clicked: there is no way to back out.

   Split into pure/framework-light pieces so this can be unit-tested without
   a real DOM (this repo has no @testing-library/jsdom):
   - `shouldDismissOnOutsidePointer` / `isDismissKey` — framework-free
     predicates.
   - `attachDismissListeners` — the actual event wiring, factored out of the
     `useEffect` body so a test can "mount" it against a tiny fake-document
     harness (see useDismissableMenu.test.js) instead of a real browser.
   - `useDismissableMenu` — thin hook wrapper; ALSO returns the composed
     `close` (fires onClose + returns focus to the trigger) so callers can
     invoke the exact same dismissal path from their own menu-item clicks,
     not just from outside-pointerdown/Escape (fix list item 13).
   ============================================================ */

/**
 * Should a pointerdown/click at `target` close the menu, given the popover
 * element (`menuEl`) and the button that opened it (`triggerEl`)? Exported
 * on its own so the RULE is testable without mounting anything.
 */
export function shouldDismissOnOutsidePointer(target, { menuEl, triggerEl } = {}) {
  if (!target) return true;
  if (menuEl && typeof menuEl.contains === 'function' && menuEl.contains(target)) return false;
  if (triggerEl && typeof triggerEl.contains === 'function' && triggerEl.contains(target)) return false;
  return true;
}

export function isDismissKey(key) {
  return key === 'Escape' || key === 'Esc';
}

/**
 * Wires the actual dismiss listeners onto `target` (real `document` in the
 * browser; an injectable fake in tests) and returns a cleanup function that
 * removes them. `close` is called at most once per dismissal — the caller
 * (useDismissableMenu below) is the one that composes it from onClose +
 * focus-return, so this function stays a pure "when do these two DOM events
 * fire `close`" wiring layer.
 *
 * @param {() => void} close
 * @param {object} refs
 * @param {React.RefObject} [refs.menuRef]     ref on the popover element
 * @param {React.RefObject} [refs.triggerRef]  ref on the button that opened it
 * @param {{ addEventListener, removeEventListener }} [refs.target]
 *   defaults to the global `document`; tests pass a fake with the same two
 *   methods so this can run outside a browser/jsdom.
 */
export function attachDismissListeners(close, { menuRef, triggerRef, target } = {}) {
  const doc = target || (typeof document !== 'undefined' ? document : null);
  if (!doc) return () => {};

  const onPointerDown = (e) => {
    if (shouldDismissOnOutsidePointer(e.target, { menuEl: menuRef?.current, triggerEl: triggerRef?.current })) {
      close();
    }
  };
  const onKeyDown = (e) => {
    if (isDismissKey(e.key)) { e.preventDefault?.(); close(); }
  };

  // Capture phase, so this runs before any bubbling click handler on
  // whatever was clicked.
  doc.addEventListener('pointerdown', onPointerDown, true);
  doc.addEventListener('keydown', onKeyDown, true);
  return () => {
    doc.removeEventListener('pointerdown', onPointerDown, true);
    doc.removeEventListener('keydown', onKeyDown, true);
  };
}

/**
 * @param {boolean} isOpen        whether the menu this hook governs is open
 * @param {() => void} onClose    close it (e.g. `() => setMenuId(null)`)
 * @param {object} refs
 * @param {React.RefObject} refs.menuRef     ref on the popover element
 * @param {React.RefObject} refs.triggerRef  ref on the button that opened it
 *   (the caller stashes `event.currentTarget` into this ref inside its own
 *   toggle onClick — see BrandKitScreen.jsx)
 * @returns {() => void} `close` — fires `onClose` then returns focus to the
 *   trigger. Fix list item 13: pass this to every menu-item's onClick (not
 *   a bare `setMenuId(null)`) so choosing an action returns focus exactly
 *   like dismissing the menu any other way does.
 *
 * Closes on: a pointerdown outside both menuRef and triggerRef (capture
 * phase); Escape; a menu-item click that calls the returned `close`; and —
 * for free, via the effect's own cleanup — unmount or a route change that
 * unmounts the screen. Toggling the trigger a second time to close is the
 * CALLER's job (its existing onClick already flips `isOpen`); this hook
 * only handles dismissal from OUTSIDE that toggle (plus the close it hands
 * back for menu items to call directly).
 */
export function useDismissableMenu(isOpen, onClose, { menuRef, triggerRef } = {}) {
  const close = useCallback(() => {
    onClose();
    triggerRef?.current?.focus?.();
  }, [onClose, triggerRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return attachDismissListeners(close, { menuRef, triggerRef });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, close, menuRef, triggerRef]);

  return close;
}

export default useDismissableMenu;
